import DashboardStatisticsAllPeriod from './DashboardStatisticsAllPeriod';
import DashboardCrMutuiLeadChart from './DashboardCrMutuiLeadChart';
import DashboardCrTotalLeadsChart from './DashboardCrTotalLeadsChart';
import DashboardRevenuePerLeadChart from './DashboardRevenuePerLeadChart';
import DashboardFunnelPerStage from './DashboardFunnelPerStage';
import { BenchmarkStatistics, Statistics } from '@tassomutuo-backoffice/types';
import { useState } from 'react';
import DashboardReceivedLeadsChart from './DashboardReceivedLeadsChart';
import DashboardEstimatedMortgagesChart from './DashboardEstimatedMortgagesChart';
import DashboardEstimatedRevenueChart from './DashboardEstimatedRevenueChart';
import DashboardEffectiveRevenueChart from './DashboardEffectiveRevenueChart';
import { Box, Typography } from '@mui/material';
import PriorityHighIcon from '@mui/icons-material/PriorityHigh';
import DashboardLegendCardModal from './DashboardLegendCardModal';
import DashboardLostRevenueChart from './DashboardLostRevenueChart';
import DashboardDropReasonsSummary from './DashboardDropReasonsSummary';
import DashboardRegionalStatisticsTable from './DashboardRegionalStatisticsTable';
import DashboardProvincialStatisticsTable from './DashboardProvincialStatisticsTable';
import DashboardConsultantStatisticsTable from './DashboardConsultantStatisticsTable';

export type CardOptions =
  | 'receivedLeads'
  | 'estimatedMortgages'
  | 'conversionRate'
  | 'conversionRateTotalLeads'
  | 'estimatedRevenue'
  | 'revenuePerLead'
  | 'effectiveRevenue'
  | 'lostRevenue';

interface DashboardContentProps {
  benchmarkStatistics: BenchmarkStatistics;
  showAllCharts?: boolean;
  isPrinting?: boolean;
}

const DashboardAdminContent = ({
  benchmarkStatistics,
  showAllCharts,
  isPrinting,
}: DashboardContentProps) => {
  const [showModalCardsLegend, setShowModalCardsLegend] = useState(false);
  const [selectedCard, setSelectedCard] =
    useState<CardOptions>('receivedLeads');

  // vado a calcolare il funnelPerStageStatistics del benchmark sommando tutti i mesi
  const benchmarkFunnelPerStageStatistics =
    benchmarkStatistics?.benchmarkStatisticsPerMonth.reduce(
      (acc, monthStats) => {
        const monthFunnelPerStage = monthStats.funnelPerStageStatistics;

        acc.totalReceivedLeads += monthFunnelPerStage.totalReceivedLeads;
        acc.confirmedAppointments += monthFunnelPerStage.confirmedAppointments;
        acc.meetingDone += monthFunnelPerStage.meetingDone;
        acc.inDevelopment += monthFunnelPerStage.inDevelopment;
        acc.advancedStage += monthFunnelPerStage.advancedStage;
        acc.concludedNotarialActs += monthFunnelPerStage.concludedNotarialActs;
        acc.dropped += monthFunnelPerStage.dropped;

        return acc;
      },
      {
        totalReceivedLeads: 0,
        confirmedAppointments: 0,
        meetingDone: 0,
        inDevelopment: 0,
        advancedStage: 0,
        concludedNotarialActs: 0,
        dropped: 0,
      }
    );

  const benchmarkStatisticsPerMonth =
    benchmarkStatistics.benchmarkStatisticsPerMonth.reduce((acc, item) => {
      acc[item.month] = item.statistics;
      return acc;
    }, {} as Record<string, Statistics>);

  return (
    <>
      {!isPrinting && (
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
          <Typography variant="h6">
            Clicca su una card per analizzare l’andamento del lavoro consulenti
          </Typography>

          <Box
            sx={{
              bgcolor: (th) => th.palette.primary.light,
              borderRadius: '5px',
              width: '30px',
              height: '30px',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              cursor: 'pointer',
              userSelect: 'none',
            }}
            onClick={() => setShowModalCardsLegend(true)}
          >
            <PriorityHighIcon fontSize="small" sx={{ color: 'white' }} />
          </Box>
        </Box>
      )}
      {benchmarkStatistics.benchmarkStatisticsAllPeriod.statistics && (
        <Box>
          <DashboardStatisticsAllPeriod
            statistics={
              benchmarkStatistics.benchmarkStatisticsAllPeriod.statistics
            }
            statisticsTrend={
              benchmarkStatistics.benchmarkStatisticsAllPeriodTrend
            }
            selectedCard={selectedCard}
            onChangeCard={setSelectedCard}
            showGoToLostRevenueDetails={false}
            isPrinting={isPrinting}
          />
        </Box>
      )}

      {benchmarkStatisticsPerMonth && (
        <>
          {(selectedCard === 'receivedLeads' || showAllCharts) && (
            <>
              <Box style={{ pageBreakAfter: 'always', breakAfter: 'page' }}>
                <DashboardReceivedLeadsChart
                  statisticsPerMonth={benchmarkStatisticsPerMonth}
                  isPrinting={isPrinting}
                  reverse={false}
                />
                {!isPrinting && (
                  <DashboardLostRevenueChart
                    statisticsPerMonth={benchmarkStatisticsPerMonth}
                    isPrinting={isPrinting}
                    reverse={false}
                  />
                )}
              </Box>
              {benchmarkStatistics.benchmarkStatisticsByRegion && (
                <Box style={{ pageBreakAfter: 'always', breakAfter: 'page' }}>
                  <DashboardRegionalStatisticsTable
                    regionalStatistics={
                      benchmarkStatistics.benchmarkStatisticsByRegion
                    }
                    isPrinting={isPrinting}
                  />
                </Box>
              )}

              {benchmarkStatistics.benchmarkStatisticsByProvince && (
                <Box style={{ pageBreakAfter: 'always', breakAfter: 'page' }}>
                  <DashboardProvincialStatisticsTable
                    provincialStatistics={
                      benchmarkStatistics.benchmarkStatisticsByProvince
                    }
                    isPrinting={isPrinting}
                  />
                </Box>
              )}
              {benchmarkStatistics.benchmarkStatisticsByConsultant && (
                <Box style={{ pageBreakAfter: 'always', breakAfter: 'page' }}>
                  <DashboardConsultantStatisticsTable
                    consultantStatistics={
                      benchmarkStatistics.benchmarkStatisticsByConsultant
                    }
                    isPrinting={isPrinting}
                  />
                </Box>
              )}
            </>
          )}
          {(selectedCard === 'estimatedMortgages' || showAllCharts) && (
            <Box>
              <DashboardEstimatedMortgagesChart
                statisticsPerMonth={benchmarkStatisticsPerMonth}
                isPrinting={isPrinting}
                reverse={false}
              />
            </Box>
          )}
          {(selectedCard === 'conversionRate' || showAllCharts) && (
            <Box style={{ pageBreakAfter: 'always', breakAfter: 'page' }}>
              <DashboardCrMutuiLeadChart
                statisticsPerMonth={benchmarkStatisticsPerMonth}
                isPrinting={isPrinting}
                reverse={false}
              />
            </Box>
          )}
          {(selectedCard === 'conversionRateTotalLeads' || showAllCharts) && (
            <Box style={{ pageBreakAfter: 'always', breakAfter: 'page' }}>
              <DashboardCrTotalLeadsChart
                statisticsPerMonth={benchmarkStatisticsPerMonth}
                isPrinting={isPrinting}
                reverse={false}
              />
            </Box>
          )}
          {(selectedCard === 'revenuePerLead' || showAllCharts) && (
            <Box>
              <DashboardRevenuePerLeadChart
                statisticsPerMonth={benchmarkStatisticsPerMonth}
                isPrinting={isPrinting}
                reverse={false}
              />
            </Box>
          )}
          {(selectedCard === 'estimatedRevenue' || showAllCharts) && (
            <Box style={{ pageBreakAfter: 'always', breakAfter: 'page' }}>
              <DashboardEstimatedRevenueChart
                statisticsPerMonth={benchmarkStatisticsPerMonth}
                isPrinting={isPrinting}
                reverse={false}
              />
            </Box>
          )}
          {(selectedCard === 'effectiveRevenue' || showAllCharts) && (
            <Box>
              <DashboardEffectiveRevenueChart
                statisticsPerMonth={benchmarkStatisticsPerMonth}
                isPrinting={isPrinting}
                reverse={false}
                hideLostRevenue
              />
            </Box>
          )}
          {(selectedCard === 'lostRevenue' || showAllCharts) && (
            <Box style={{ pageBreakAfter: 'always', breakAfter: 'page' }}>
              <DashboardLostRevenueChart
                statisticsPerMonth={benchmarkStatisticsPerMonth}
                isPrinting={isPrinting}
                reverse={false}
              />
            </Box>
          )}
        </>
      )}

      {benchmarkFunnelPerStageStatistics && (
        <Box style={{ pageBreakAfter: 'always', breakAfter: 'page' }}>
          <DashboardFunnelPerStage
            funnelPerStageStatistics={benchmarkFunnelPerStageStatistics}
            isPrinting={isPrinting}
          />
        </Box>
      )}

      <DashboardDropReasonsSummary
        isPrinting={isPrinting}
        droppedReasons={
          benchmarkStatistics?.benchmarkStatisticsAllPeriod.droppedReasons
        }
      />

      {showModalCardsLegend && (
        <DashboardLegendCardModal
          open
          onClose={() => setShowModalCardsLegend(false)}
        />
      )}
    </>
  );
};

export default DashboardAdminContent;
